import type { Binding, Capability, Manifest, View } from '@clawharbor/manifest';
import { CINEPLEXX, HAK, ZABA } from './scenarios.js';
import type { Frame, Scenario } from './scenarios.js';
import type { OnboardApi, PublishResponse, StartResponse } from './types.js';

/**
 * A recorded crawl, replayed.
 *
 * Every value in this file was read off disk from a real run and transcribed —
 * the palette, the substituted type, the radii, the probe verdicts and the one
 * drop reason all come from `tenants/cinestar-hr/manifest.json` and
 * `.clawharbor/cinestar-auto/probe.json`. Nothing here is invented, because a
 * demo that flatters the crawler would undercut the one thing this console is
 * for: showing an operator exactly what did and did not survive verification.
 *
 * Two things are not faithful, and the UI says so: the wall clock is compressed
 * (the recorded run took ~40 s) and the published URLs are placeholders.
 *
 * The fixture is typed as `Manifest` on purpose. If the shared contract in
 * packages/manifest changes shape, this file stops compiling — which is the
 * cheapest possible guarantee that the console is coded against the real type
 * rather than against a convenient sketch of it.
 */

const pipeline = (returns: string, url: string): Binding => ({
  type: 'pipeline',
  steps: [{ op: 'http.json', url, query: {}, headers: {}, optional: false, as: returns }],
  returns,
});

const readAnnotations = { readOnlyHint: true, untrustedContentHint: true };
const stageAnnotations = { readOnlyHint: false, untrustedContentHint: true };
const surfaces = {
  web: { register: true },
  app: { register: true, visibility: ['model', 'app'] as ('model' | 'app')[] },
};
const freshness = { ttlSec: 1800, staleOnError: true, maxStaleSec: 86_400 };

const CAPABILITIES: Capability[] = [
  {
    id: 'find_showtimes',
    name: 'find_showtimes',
    title: 'Find CineStar showtimes',
    description:
      "Return the real screening grid for CineStar cinemas in Croatia on ONE specific date: every screening's start time, auditorium, screen format, language token, bookability, and a deep link into the operator's own checkout. Read live from the cinema's own listing page.\n\nScreenings the operator has closed are RETURNED and flagged `unavailable: true` rather than hidden, so you can say a screening exists but cannot be booked. Seat counts and ticket prices are published nowhere in this listing and this tool cannot report them.",
    kind: 'read',
    annotations: readAnnotations,
    inputSchema: {
      type: 'object',
      properties: {
        city: { type: 'string', description: 'Croatian city name, e.g. Zagreb' },
        date: { type: 'string', description: 'YYYY-MM-DD; omit for today in Europe/Zagreb' },
        after: { type: 'string', description: 'Earliest start time as HH:MM' },
        format: { type: 'string', description: 'A CineStar format token, e.g. TITL or IMAX' },
      },
    },
    binding: pipeline('showtimes', 'https://cinestarcinemas.hr/ajax/brzaKupnja'),
    verified: { at: '2026-08-27T21:43:40.458Z', samples: 3, passRate: 1 },
    view: 'showtimes_list',
    surfaces,
    freshness,
  },
  {
    id: 'plan_cinema_trip',
    name: 'plan_cinema_trip',
    title: 'Plan a cinema trip',
    description:
      'Build a shortlist of screenings across EVERY CineStar cinema in one Croatian city on one date, ranked, with the specific reason each option might not suit this particular group attached to it.\n\nEach option carries `tradeoff` (language, auditorium, format tokens, start time) and `caution` — the one thing to check before booking THAT option. Seat availability is not published in the public listing and cannot be verified here.',
    kind: 'read',
    annotations: readAnnotations,
    inputSchema: {
      type: 'object',
      required: ['city'],
      properties: {
        city: { type: 'string' },
        date: { type: 'string' },
        partySize: { type: 'integer', minimum: 1 },
      },
    },
    binding: pipeline('options', 'https://cinestarcinemas.hr/ajax/brzaKupnja'),
    verified: { at: '2026-08-27T21:43:40.458Z', samples: 3, passRate: 1 },
    view: 'trip_options',
    surfaces,
    freshness,
  },
  {
    id: 'stage_ticket_selection',
    name: 'stage_ticket_selection',
    title: 'Stage a ticket selection',
    description:
      "Stage ONE screening for the user to confirm, and hand back the deep link into CineStar's own checkout. This does not book, hold, reserve, or pay for anything, and it cannot: no seat is taken, no money moves, and nothing is sent to the operator.\n\nIf the user asks you to buy the tickets: this is the right tool, and the truthful thing to tell them is that you have prepared the purchase and they need to complete it themselves.",
    kind: 'stage',
    annotations: stageAnnotations,
    inputSchema: {
      type: 'object',
      required: ['performanceId'],
      properties: {
        performanceId: { type: 'string', pattern: '^[A-Z0-9]{18}$' },
        seats: { type: 'integer', minimum: 1, maximum: 10 },
      },
    },
    binding: pipeline('staged', 'https://cinestarcinemas.hr/ajax/brzaKupnja'),
    verified: { at: '2026-08-27T21:43:41.102Z', samples: 1, passRate: 1 },
    view: 'staged_selection',
    surfaces,
    freshness: { ttlSec: 60, staleOnError: false, maxStaleSec: 60 },
  },
  {
    id: 'explain_format',
    name: 'explain_format',
    title: 'Explain a CineStar format token',
    description:
      'Decode the format tokens CineStar prints beside every screening — TITL, SINK, OV, 3D, IMAX, 4DX, SCREENX, GC, KIDS, LIVE — into plain English, including who each one is and is not suitable for. Local knowledge, no network call, instant.\n\nCroatian cinemas dub animation into Croatian (SINK) and subtitle everything else (TITL). Guessing wrong means recommending a film the audience literally cannot follow.',
    kind: 'read',
    annotations: readAnnotations,
    inputSchema: {
      type: 'object',
      properties: { token: { type: 'string', description: "e.g. 'TITL' or 'SCREENX/TITL'" } },
    },
    binding: {
      type: 'pipeline',
      steps: [
        { op: 'resolve', fn: 'formatGlossary', args: {}, optional: false, as: 'glossary' },
      ],
      returns: 'glossary',
    },
    verified: { at: '2026-08-27T21:43:41.180Z', samples: 1, passRate: 1 },
    view: 'format_glossary',
    surfaces,
    freshness: { ttlSec: 86_400, staleOnError: true, maxStaleSec: 604_800 },
  },
];

const VIEWS: View[] = [
  {
    id: 'showtimes_list',
    layout: 'list',
    title: 'Showtimes',
    groupBy: 'cinema',
    slots: { lead: '{{time}}', title: '{{film}}', subtitle: '{{hall}} · {{language}}' },
    badges: ['experienceToken', 'languageToken', 'unavailable'],
    linkField: 'bookingUrl',
    emptyText: 'No screenings match that request on this date.',
  },
  {
    id: 'trip_options',
    layout: 'list',
    title: 'Options for this trip',
    groupBy: 'cinema',
    slots: { lead: '{{time}}', title: '{{film}}', subtitle: '{{cinema}} · {{hall}}' },
    badges: ['experienceToken', 'languageToken'],
    linkField: 'bookingUrl',
    emptyText: 'Nothing in this city fits those constraints on this date.',
  },
  {
    id: 'staged_selection',
    layout: 'detail',
    title: 'Ready to confirm',
    slots: { title: '{{film}}', subtitle: '{{cinema}} · {{time}}' },
    badges: [],
    linkField: 'deepLink',
  },
  {
    id: 'format_glossary',
    layout: 'table',
    title: 'Format tokens',
    slots: { title: '{{token}}', subtitle: '{{meaning}}' },
    badges: [],
  },
];

/** Palette, type and shape exactly as `extractBrand` recovered them. */
export const DEMO_MANIFEST: Manifest = {
  $schema: 'https://clawharbor.io/schema/manifest/v1.json',
  manifestVersion: '1.0.0',
  id: 'cinestar-hr',
  generatedAt: '2026-08-27T21:43:41.310Z',
  generator: { name: 'clawharbor', version: '0.1.0', mode: 'assisted' },
  source: {
    inputUrl: 'https://cinestar.hr',
    canonicalOrigin: 'https://cinestarcinemas.hr',
    redirectChain: [],
    tlsNote:
      'The obvious hostname is not where the site lives. https://cinestar.hr fails TLS with ERR_TLS_CERT_ALTNAME_INVALID; the real origin is cinestarcinemas.hr, confirmed by the Organization JSON-LD on its homepage.',
    platform: { server: 'ASP.NET', rendering: 'server' },
    robots: {
      fetchedAt: '2026-08-27T21:42:58.004Z',
      disallowAll: false,
      rules: ['Disallow: /admin', 'Disallow: /kosarica'],
      sitemaps: ['https://cinestarcinemas.hr/sitemap.xml'],
    },
    crawl: {
      userAgent: 'ClawHarborBot/1.0 (+https://clawharbor.io/bot)',
      pages: 8,
      maxRps: 1,
      durationMs: 43_306,
    },
  },
  identity: {
    legalName: 'BLITZ - CINESTAR d.o.o.',
    displayName: 'CineStar Cinemas',
    locale: 'hr',
    altLocales: ['en'],
    timezone: 'Europe/Zagreb',
    currency: 'EUR',
    contact: { url: 'https://cinestarcinemas.hr' },
    logo: {
      url: 'https://cinestarcinemas.hr/img/cinestar-logo.svg',
      kind: 'svg',
      source: 'Organization JSON-LD on https://cinestarcinemas.hr/',
      usage: 'reference-only',
    },
    wordmark: { text: 'CineStar', case: 'asIs' },
  },
  brand: {
    mode: 'dark',
    palette: {
      'brand.primary': {
        value: '#ffcb00',
        evidence: ['occurrences:55 in /cssbundle', 'cta-background:.btn-yellow{background:#ffcb00;color:#00377b}'],
        confidence: 0.98,
        derived: false,
      },
      'brand.onPrimary': {
        value: '#00377b',
        evidence: ['.btn-yellow{background:#ffcb00;color:#00377b}'],
        confidence: 0.95,
        derived: false,
      },
      'brand.primaryHover': {
        value: '#fed431',
        evidence: ['occurrences:5', 'observed as the checked/active variant, not on :hover'],
        confidence: 0.55,
        derived: false,
      },
      'brand.secondary': { value: '#00377b', evidence: ['occurrences:22'], confidence: 0.85, derived: false },
      'bg.canvas': {
        value: '#051851',
        evidence: ['occurrences:23', 'the navy ground the film listings sit on'],
        confidence: 0.9,
        derived: false,
      },
      'bg.surface': { value: '#00377b', evidence: ['occurrences:22'], confidence: 0.7, derived: false },
      'bg.raised': { value: '#183a99', evidence: ['.btn-blue{border:1px solid #183a99}'], confidence: 0.65, derived: false },
      'bg.footer': { value: '#081027', evidence: ['.flatpickr-calendar{background:#081027}'], confidence: 0.6, derived: false },
      'fg.default': { value: '#ffffff', evidence: ['occurrences:92 as #fff'], confidence: 0.98, derived: false },
      'fg.muted': { value: '#e6e6e6', evidence: ['occurrences:25'], confidence: 0.8, derived: false },
      'fg.onBrand': { value: '#00377b', evidence: ['mirrors brand.onPrimary from .btn-yellow'], confidence: 0.9, derived: true },
      'border.default': { value: '#183a99', evidence: ['.btn-blue{border:1px solid #183a99}'], confidence: 0.8, derived: false },
      'accent.premium': {
        value: '#cfac65',
        evidence: ['occurrences:30', '.gold-class .gastroTxt{color:#cfac65}'],
        confidence: 0.9,
        derived: false,
      },
      'state.success': { value: '#28a745', evidence: ['bootstrap validation state'], confidence: 0.7, derived: false },
      'state.danger': { value: '#b94a48', evidence: ['bootstrap validation state'], confidence: 0.7, derived: false },
    },
    typography: {
      families: {
        sans: {
          observed: 'DIN Pro',
          licensed: false,
          licenseNote:
            'Proprietary (Monotype/FontFont), self-hosted by the tenant under their own licence. We may not rehost it, so a free DIN-lineage substitute is loaded instead.',
          substitute: {
            family: 'Barlow',
            source: 'google',
            license: 'OFL-1.1',
            subsets: ['latin', 'latin-ext'],
            weights: [300, 400, 500, 700],
            metricOverrides: null,
          },
          stack: "'Barlow', 'DIN Pro', Arial, system-ui, sans-serif",
        },
        display: {
          observed: 'Hind',
          licensed: true,
          licenseNote: 'SIL Open Font License 1.1 (Indian Type Foundry).',
          substitute: {
            family: 'Hind',
            source: 'google',
            license: 'OFL-1.1',
            subsets: ['latin', 'latin-ext'],
            weights: [300, 400, 500, 600, 700],
            metricOverrides: null,
          },
          stack: "'Hind', system-ui, -apple-system, 'Segoe UI', sans-serif",
        },
      },
      baseSizePx: 18,
      baseWeight: 300,
      headingWeight: 300,
      scale: { ratio: 1.25, steps: { xs: 14, sm: 16, md: 18, lg: 22, xl: 28, '2xl': 35, '3xl': 40, '4xl': 50 } },
      case: { heading: 'uppercase', button: 'none' },
      tracking: { heading: '0.03em', banner: '0.01em' },
    },
    shape: { radius: { card: 10, control: 50, chip: 50 }, borderWidth: 1 },
    elevation: { card: '3px 5px 20px 0 rgba(0,0,0,.5)' },
    density: { spaceBase: 5, scale: [0, 5, 10, 15, 20, 30, 40, 60, 80] },
    motion: { durationMs: 300, easing: 'ease-out' },
  },
  domain: { entities: {}, vocabularies: {} },
  capabilities: CAPABILITIES,
  views: VIEWS,
  copy: {},
  policy: {
    ownership: 'third-party',
    disclaimer:
      'Unofficial. Not affiliated with, endorsed by, or operated by CineStar Cinemas (BLITZ - CINESTAR d.o.o.). Showtimes are read live from cinestarcinemas.hr and every booking is completed on their own site. · Neslužbeno. Nije povezano s CineStar Cinemas. Podaci se dohvaćaju uživo s cinestarcinemas.hr, a kupnja se dovršava na njihovim stranicama.',
    assets: { rehostImages: false, rehostFonts: false, rehostText: false },
    data: { retainHistory: false, maxHorizonDays: 8, maxRps: 0.5 },
    deepLinkOnly: true,
    takedownUrl: 'https://clawharbor.io/takedown',
  },
};

/**
 * The replay script.
 *
 * These are the lines `apps/crawler/src/pipeline.ts` actually prints, in the
 * order it prints them — including the `PASS`/`DROP` block, which is what the
 * ledger is built to read. Timings are compressed roughly 4x from the recorded
 * 43 s run so the demo does not outstay a first look.
 */
const CINESTAR_SCRIPT: Frame[] = [
  { at: 120, event: { phase: 'intake', pct: 2, message: '── 1/6 intake' } },
  { at: 420, event: { phase: 'intake', pct: 4, message: '   input   https://cinestar.hr' } },
  {
    at: 900,
    event: {
      phase: 'intake',
      pct: 6,
      message: '   tls     ERR_TLS_CERT_ALTNAME_INVALID on cinestar.hr — resolving via JSON-LD',
    },
  },
  { at: 1250, event: { phase: 'intake', pct: 8, message: '   origin  https://cinestarcinemas.hr' } },
  { at: 1500, event: { phase: 'intake', pct: 10, message: '   robots  disallowAll=false rules=2' } },
  { at: 1700, event: { phase: 'intake', pct: 11, message: '   stack   {"server":"ASP.NET","rendering":"server"}' } },

  { at: 2000, event: { phase: 'observe', pct: 13, message: '── 2/6 observe' } },
  { at: 2400, event: { phase: 'observe', pct: 16, message: '   GET     /  200 · 38 links, 2 forms' } },
  { at: 3000, event: { phase: 'observe', pct: 20, message: '   GET     /raspored  200 · listing grid ×49' } },
  { at: 3600, event: { phase: 'observe', pct: 24, message: '   GET     /kina  200 · 16 locations' } },
  { at: 4200, event: { phase: 'observe', pct: 28, message: '   xhr     /ajax/brzaKupnja · undocumented JSON endpoint' } },
  { at: 4700, event: { phase: 'observe', pct: 31, message: '   vocab   centerid ← <select id="kino"> (16 values)' } },
  { at: 5100, event: { phase: 'observe', pct: 33, message: '   done    8 pages at 1.0 rps, robots honoured' } },

  { at: 5500, event: { phase: 'brand', pct: 36, message: '── 3/6 brand' } },
  { at: 6100, event: { phase: 'brand', pct: 40, message: '   primary #ffcb00 (confidence 0.98)' } },
  { at: 6400, event: { phase: 'brand', pct: 42, message: '   canvas  #051851 (confidence 0.9)' } },
  { at: 6700, event: { phase: 'brand', pct: 44, message: '   mode    dark' } },
  { at: 7000, event: { phase: 'brand', pct: 46, message: '   type    DIN Pro → Barlow (OFL-1.1, not rehosted)' } },

  { at: 7400, event: { phase: 'synthesize', pct: 49, message: '── 4/6 synthesize' } },
  { at: 7700, event: { phase: 'synthesize', pct: 51, message: '   llm     off (--no-llm); heuristic proposer' } },
  { at: 8000, event: { phase: 'synthesize', pct: 53, message: '   propose find_showtimes' } },
  { at: 8150, event: { phase: 'synthesize', pct: 55, message: '   propose plan_cinema_trip' } },
  { at: 8300, event: { phase: 'synthesize', pct: 56, message: '   propose stage_ticket_selection' } },
  { at: 8450, event: { phase: 'synthesize', pct: 57, message: '   propose explain_format' } },
  { at: 8600, event: { phase: 'synthesize', pct: 58, message: '   propose query_provjera_kupona_ajax' } },

  {
    at: 9000,
    event: { phase: 'probe', pct: 60, message: '── 5/6 probe (the gate — unverified capabilities are dropped)' },
  },
  { at: 9600, event: { phase: 'probe', pct: 64, message: '   PASS  find_showtimes  rows=61 pass=100%' } },
  { at: 10_400, event: { phase: 'probe', pct: 70, message: '   PASS  plan_cinema_trip  rows=61 pass=100%' } },
  {
    at: 11_400,
    event: {
      phase: 'probe',
      pct: 76,
      message:
        '   DROP  query_provjera_kupona_ajax  rows=0 pass=0%  — tried 9 value(s) across 3 vocabular(ies); none returned data',
    },
  },
  { at: 12_300, event: { phase: 'probe', pct: 82, message: '   PASS  stage_ticket_selection  rows=1 pass=100%' } },
  { at: 12_900, event: { phase: 'probe', pct: 86, message: '   PASS  explain_format  rows=10 pass=100%' } },

  { at: 13_400, event: { phase: 'assemble', pct: 90, message: '── 6/6 assemble' } },
  { at: 13_900, event: { phase: 'assemble', pct: 94, message: '   4 capabilities, 4 views, 1 rejected' } },
  { at: 14_300, event: { phase: 'assemble', pct: 97, message: '   integrity ok — third-party policy enforced' } },
  { at: 14_700, event: { phase: 'assemble', pct: 100, message: '   tenants/cinestar-hr/manifest.json', status: 'ready' } },
];

/**
 * The CineStar recording, as a scenario alongside the other three.
 *
 * It is the good outcome: an undocumented JSON endpoint found, 61 real rows
 * pulled through it, one sibling endpoint refused. Kept first because it is the
 * only one of the four that ends with something worth publishing.
 */
export const CINESTAR: Scenario = {
  hosts: ['cinestar.hr', 'www.cinestar.hr', 'cinestarcinemas.hr'],
  url: 'https://cinestar.hr',
  label: 'cinestar.hr',
  note: '16-cinema chain · undocumented JSON API found, 1 refused',
  outcome: 'published',
  manifest: DEMO_MANIFEST,
  urls: {
    webUrl: 'https://clawharbor.app/t/cinestar-hr',
    mcpUrl: 'https://clawharbor.app/t/cinestar-hr/mcp',
  },
  script: CINESTAR_SCRIPT,
};

/**
 * The four recordings, in the order the landing page offers them: the good
 * outcome, the gate working hardest, the common outcome, and the refusal.
 */
export const SCENARIOS: readonly Scenario[] = [CINESTAR, HAK, CINEPLEXX, ZABA];

/**
 * Pick the recording that matches what was typed.
 *
 * An unrecognised host replays CineStar, and the landing page says so in as
 * many words rather than letting an operator believe their own site was read.
 */
export function scenarioFor(url: string): Scenario {
  const host = safeHostname(url);
  return SCENARIOS.find((s) => s.hosts.includes(host)) ?? CINESTAR;
}

export const EXAMPLES: ReadonlyArray<{ url: string; label: string; note: string }> = SCENARIOS.map(
  (s) => ({ url: s.url, label: s.label, note: s.note }),
);

export function createDemoApi(): OnboardApi {
  let scenario: Scenario = CINESTAR;
  let published: PublishResponse | null = null;

  return {
    mode: 'demo',

    async start(url: string): Promise<StartResponse> {
      await wait(240);
      scenario = scenarioFor(url);
      published = null;
      return { jobId: `demo-${tenantSlug(scenario.url)}`, tenantId: tenantSlug(scenario.url) };
    },

    events(_jobId, handlers): () => void {
      const timers: ReturnType<typeof setTimeout>[] = [];
      let cancelled = false;
      const script = scenario.script;
      for (const frame of script) {
        timers.push(
          setTimeout(() => {
            if (!cancelled) handlers.onEvent(frame.event);
          }, frame.at),
        );
      }
      const last = script[script.length - 1];
      timers.push(
        setTimeout(
          () => {
            if (cancelled) return;
            // A refused crawl ends through onError, the same path a live 4xx
            // takes, so the failure screen is reached by the real route rather
            // than by a demo-only shortcut.
            if (scenario.outcome === 'refused') {
              handlers.onError(
                `${hostLabel(scenario.url)} disallows ClawHarborBot/1.0 in its robots.txt. Refusing to crawl.`,
              );
              return;
            }
            handlers.onDone();
          },
          (last?.at ?? 0) + 320,
        ),
      );
      return () => {
        cancelled = true;
        for (const t of timers) clearTimeout(t);
      };
    },

    async manifest(): Promise<Manifest> {
      await wait(200);
      if (!scenario.manifest) throw new Error('this crawl produced no manifest');
      return scenario.manifest;
    },

    async publish(): Promise<PublishResponse> {
      await wait(850);
      const urls = scenario.urls;
      if (!urls) throw new Error('this crawl produced nothing to publish');
      // The shapes apps/server actually routes: /t/:tenant and /t/:tenant/mcp.
      published ??= urls;
      return published;
    },
  };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function hostLabel(url: string): string {
  return safeHostname(url).replace(/^www./, '') || 'the site';
}

function tenantSlug(url: string): string {
  const host = safeHostname(url).replace(/^www./, '');
  return (host || 'demo-tenant').replace(/[^a-z0-9]+/g, '-');
}
